import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Seo from '../../components/Seo'
import { api } from '../../api/client'

type Details = Awaited<ReturnType<typeof api.meetingDetails>>
type Slot = Awaited<ReturnType<typeof api.meetingSlots>>['slots'][number]
export default function MeetingChange() {
  const location=useLocation()
  const token=new URLSearchParams(location.hash.slice(1)).get('token') ?? ''
  const [details,setDetails]=useState<Details|null>(null)
  const [slots,setSlots]=useState<Slot[]>([])
  const [selected,setSelected]=useState('')
  const [error,setError]=useState('')
  const [busy,setBusy]=useState(false)
  const [done,setDone]=useState(false)
  const [searched,setSearched]=useState(false)
  const pending=useRef(false)
  useEffect(()=>{
    let live=true
    setDetails(null);setError('');setDone(false);setSlots([]);setSelected('');setSearched(false)
    if(!token){setError('Open the meeting link in your invitation.');return}
    api.meetingDetails(token).then(value=>{if(live)setDetails(value)}).catch(e=>{if(live)setError(e.message)})
    return()=>{live=false}
  },[token])
  const when=(value:string)=>new Date(value).toLocaleString(undefined,{timeZone:details?.time_zone,dateStyle:'full',timeStyle:'short'})
  async function findTimes(){
    if(pending.current)return
    pending.current=true;setBusy(true);setError('');setSelected('');setSlots([])
    const start=new Date(),end=new Date(start);end.setDate(end.getDate()+30)
    try{setSlots((await api.meetingSlots(token,start.toISOString(),end.toISOString())).slots);setSearched(true)}
    catch(e){setError(e instanceof Error?e.message:'Could not load available times.')}
    finally{pending.current=false;setBusy(false)}
  }
  async function confirm(){
    if(!details || pending.current)return
    pending.current=true;setBusy(true);setError('')
    try{await api.changeMeeting(token,details.action,selected || undefined);setDone(true)}
    catch(e){setError(e instanceof Error?e.message:'Could not confirm the change.');setSelected('')}
    finally{pending.current=false;setBusy(false)}
  }
  return <><Seo title="Manage your meeting" noindex/>
    <section className="hero"><h1>Manage your meeting</h1></section>
    <section className="section" style={{maxWidth:'44rem'}}>
      {error && <p className="notice crit" role="alert">{error} Reply to your invitation if you need help.</p>}
      {done ? <p className="notice" role="status">{details?.action==='cancel'?'Your meeting has been canceled.':'Your new meeting time is reserved.'} A calendar update is queued for you and the analyst.</p> : details ? <>
        <p>Your current meeting is on <strong>{when(details.start_at)}</strong> ({details.time_zone}).</p>
        {details.action==='cancel' ? <><p>Confirm below to cancel this meeting.</p><button className="btn" disabled={busy} onClick={confirm}>{busy?'Canceling…':'Confirm cancellation'}</button></> : <>
          <p>Choose a new time in the next 30 days. Your current reservation stays in place until you confirm a change.</p>
          <button className="btn secondary" disabled={busy} onClick={findTimes}>{busy?'Working…':searched?'Refresh available times':'Find available times'}</button>
          {searched && !slots.length && !busy && <p>No available times were found. Reply to your invitation to arrange another time.</p>}
          <form onSubmit={e=>{e.preventDefault();void confirm()}}>
            {slots.map(slot=><label key={slot.start} style={{display:'block',padding:'.75rem 0'}}><input type="radio" name="new-time" value={slot.start} checked={selected===slot.start} disabled={busy} onChange={()=>setSelected(slot.start)}/> {when(slot.start)} ({slot.time_zone})</label>)}
            {!!slots.length && <button className="btn" disabled={busy || !selected}>{busy?'Confirming…':'Confirm new time'}</button>}
          </form>
        </>}
      </> : !error && <p role="status">Loading meeting…</p>}
    </section>
  </>
}
